import React, { useState } from "react";
import ComparativaReformas from "../components/RenewComparison";

const PropertyReformDetails = ({ propiedad, modoComparacion, setModoComparacion, indicadores, soloIA, existenReformasIA }) => {
    const [expandidas, setExpandidas] = useState({});

    const toggleExpand = (id) => {
        setExpandidas((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const obtenerIndicador = (reformaId) => {
        const todas = [...(indicadores?.venta || []), ...(indicadores?.alquiler || [])];
        return todas.find(i => Number(i.reformaPropiedadId) === Number(reformaId));
    };

    // Filter out IA reform types and ensure reformas is an array
    const reformasValidas = Array.isArray(propiedad?.reformas) 
        ? propiedad.reformas.filter(reforma => 
            reforma && 
            reforma.tipo_reforma && 
            !reforma.tipo_reforma.includes('IA') &&
            reforma.coste_total_reforma !== 'NaN'
        )
        : [];

    const reformasAgrupadas = reformasValidas.reduce((acc, reforma) => {
        const tipo = reforma.tipo_reforma;
        if (!acc[tipo]) acc[tipo] = [];
        acc[tipo].push(reforma);
        return acc;
    }, {});

    if (modoComparacion) {
        return (
            <div>
                <ComparativaReformas reformas={reformasValidas} indicadores={indicadores} />
                <div className="text-center mt-6">
                    <button
                        className="bg-gray-900 hover:bg-gray-800 text-white font-medium py-2 px-4 rounded-lg"
                        onClick={() => setModoComparacion(false)}
                    >
                        Volver a vista individual
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            {!existenReformasIA && (
                <div className="text-center mb-4 text-gray-500 text-sm italic animate-fade-in">
                    ⚡ Esta propiedad aún no ha sido analizada por IA
                </div>
            )}

            {/* Listado de reformas */}
            {Object.entries(reformasAgrupadas).map(([tipo, reformas]) => (
                <div key={tipo} className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{tipo}</h3>
                    {reformas.map((reforma) => {
                        const indicador = obtenerIndicador(reforma.reforma_id);
                        const resumen = `${reforma.constructor} · ${reforma.tipo_reforma} · ${reforma.actuaciones.length} actuaciones`;

                        const actuacionesFinales = reforma.actuaciones.map(act => {
                            const cantidadOriginal = parseFloat(act.cantidad);
                            const sugerida = act.sugeridoIA ? parseFloat(act.cantidadSugeridaIA) : null;
                            const usarSugerencia = soloIA && act.sugeridoIA;
                            const cantidad = usarSugerencia && sugerida !== null ? sugerida : cantidadOriginal;

                            // Calcular costes originales basados en la cantidad original
                            const costeMaterialOriginal = parseFloat(act.coste_material_unitario) * cantidadOriginal;
                            const costeObraOriginal = parseFloat(act.coste_mano_obra_unitario) * parseFloat(act.horas_mano_obra) * cantidadOriginal;
                            const totalOriginal = costeMaterialOriginal + costeObraOriginal;

                            // Calcular costes con la cantidad sugerida por IA
                            const costeMaterialIA = parseFloat(act.coste_material_unitario) * cantidad;
                            const costeObraIA = parseFloat(act.coste_mano_obra_unitario) * parseFloat(act.horas_mano_obra) * cantidad;
                            const totalIA = costeMaterialIA + costeObraIA;

                            const hayCambioCantidad = usarSugerencia && sugerida !== cantidadOriginal;
                            const hayCambioMaterial = usarSugerencia && sugerida !== cantidadOriginal;
                            const hayCambioObra = usarSugerencia && sugerida !== cantidadOriginal;

                            return {
                                ...act,
                                cantidadVisible: cantidad,
                                costeMaterialVisible: usarSugerencia ? costeMaterialIA : costeMaterialOriginal,
                                costeObraVisible: usarSugerencia ? costeObraIA : costeObraOriginal,
                                totalVisible: usarSugerencia ? totalIA : totalOriginal,
                                costeMaterialOriginal: costeMaterialOriginal,
                                costeObraOriginal: costeObraOriginal,
                                totalOriginal: totalOriginal,
                                hayCambioCantidad,
                                hayCambioMaterial,
                                hayCambioObra
                            };
                        });

                        const totalOriginal = reforma.coste_total_reforma_original || reforma.coste_total_reforma;
                        const totalIA = actuacionesFinales.reduce((sum, act) =>
                            soloIA && !act.sugeridoIA ? sum : sum + act.costeMaterialVisible + act.costeObraVisible, 0);

                        return (
                            <div key={reforma.reforma_id} className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
                                <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleExpand(reforma.reforma_id)}>
                                    <div>
                                        <p className="text-md font-semibold">{resumen}</p>
                                        <p className="text-sm text-gray-600">
                                            Coste estimado: {soloIA ? (
                                            <>
                                                <span className="line-through mr-1 text-gray-400">{parseFloat(totalOriginal).toLocaleString()} €</span>
                                                <span className="text-blue-800 font-semibold">{totalIA.toLocaleString()} €</span>
                                            </>
                                        ) : `${parseFloat(totalOriginal).toLocaleString()} €`}
                                        </p>
                                    </div>
                                    <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                                        {expandidas[reforma.reforma_id] ? "Ocultar detalles" : "Ver detalles"}
                                    </button>
                                </div>

                                {expandidas[reforma.reforma_id] && (
                                    <div className="mt-4 border-t pt-2 overflow-x-auto">
                                        <table className="min-w-full table-auto text-sm text-left">
                                            <thead>
                                            <tr className="text-xs font-semibold uppercase tracking-wide">
                                                <th colSpan={3} className="px-4 py-1.5 bg-gray-50 border-b border-gray-200"></th>
                                                <th colSpan={2} className="px-4 py-1.5 bg-blue-50 text-blue-600 border-l-2 border-b border-blue-200 text-center">Material</th>
                                                <th colSpan={2} className="px-4 py-1.5 bg-amber-50 text-amber-700 border-l-2 border-b border-amber-200 text-center">Mano de obra</th>
                                                <th colSpan={1} className="px-4 py-1.5 bg-gray-100 text-gray-600 border-l-2 border-b border-gray-300 text-center">Total</th>
                                            </tr>
                                            <tr className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                                                <th className="px-4 py-2 bg-gray-50">Actuación</th>
                                                <th className="px-4 py-2 bg-gray-50 text-right">Cantidad</th>
                                                <th className="px-4 py-2 bg-gray-50">Unidad</th>
                                                <th className="px-4 py-2 bg-blue-50 border-l-2 border-blue-200 text-right">€/ud</th>
                                                <th className="px-4 py-2 bg-blue-50 text-right">Total</th>
                                                <th className="px-4 py-2 bg-amber-50 border-l-2 border-amber-200 text-right">€/ud</th>
                                                <th className="px-4 py-2 bg-amber-50 text-right">Total</th>
                                                <th className="px-4 py-2 bg-gray-100 border-l-2 border-gray-300 text-right">Total</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {actuacionesFinales.map((act, idx) => (
                                                <tr
                                                    key={idx}
                                                    className={`border-b border-gray-100 hover:bg-gray-50 ${soloIA && !act.sugeridoIA ? 'line-through text-gray-400 bg-gray-50' : ''}`}
                                                >
                                                    <td className="px-4 py-3">{act.actuacion}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums">
                                                        {act.hayCambioCantidad ? (
                                                            <>
                                                                <span className="line-through text-gray-400 mr-1">{act.cantidad}</span>
                                                                <span className="text-blue-800 font-semibold">{act.cantidadVisible}</span>
                                                            </>
                                                        ) : act.cantidadVisible}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-500">{act.unidad?.replace(/^€\//, '') ?? ''}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-gray-500 bg-blue-50/40 border-l-2 border-blue-100">
                                                        {parseFloat(act.coste_material_unitario).toLocaleString()} €
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums bg-blue-50/40">
                                                        {act.hayCambioMaterial ? (
                                                            <>
                                                                <span className="line-through text-gray-400 mr-1">{act.costeMaterialOriginal.toLocaleString()} €</span>
                                                                <span className="text-blue-800 font-semibold">{act.costeMaterialVisible.toLocaleString()} €</span>
                                                            </>
                                                        ) : `${act.costeMaterialVisible.toLocaleString()} €`}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums text-gray-500 bg-amber-50/40 border-l-2 border-amber-100">
                                                        {(parseFloat(act.coste_mano_obra_unitario) * parseFloat(act.horas_mano_obra)).toLocaleString()} €
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums bg-amber-50/40">
                                                        {act.hayCambioObra ? (
                                                            <>
                                                                <span className="line-through text-gray-400 mr-1">{act.costeObraOriginal.toLocaleString()} €</span>
                                                                <span className="text-blue-800 font-semibold">{act.costeObraVisible.toLocaleString()} €</span>
                                                            </>
                                                        ) : `${act.costeObraVisible.toLocaleString()} €`}
                                                    </td>
                                                    <td className="px-4 py-3 text-right tabular-nums font-semibold bg-gray-50 border-l-2 border-gray-200">
                                                        {act.hayCambioMaterial || act.hayCambioObra ? (
                                                            <>
                                                                <span className="line-through text-gray-400 mr-1">{act.totalOriginal.toLocaleString()} €</span>
                                                                <span className="text-blue-800 font-semibold">{act.totalVisible.toLocaleString()} €</span>
                                                            </>
                                                        ) : `${act.totalVisible.toLocaleString()} €`}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default PropertyReformDetails;